import { CURRENCY } from "@/config";

type FormatCurrencyOptions = {
  locale?: string;
  currency?: string;
};

export function formatCurrency(
  value: number,
  options: FormatCurrencyOptions = {},
) {
  const { locale = "en-GB", currency = CURRENCY } = options;

  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
  });

  return formatter.format(value);
}

type CamelCase<S extends string> =
  S extends `${infer Prefix}_${infer C}${infer Suffix}`
    ? C extends Lowercase<C>
      ? `${Prefix}${Uppercase<C>}${CamelCase<Suffix>}`
      : `${Prefix}_${C}${CamelCase<Suffix>}`
    : S;

type CamelCased<T> = T extends object
  ? {
      [K in keyof T as CamelCase<Extract<K, string>>]: T[K] extends Array<
        infer U
      >
        ? Array<CamelCased<U>>
        : T[K] extends object
          ? CamelCased<T[K]>
          : T[K];
    }
  : T;

export function camelCaseKeys<T extends object>(obj: T): CamelCased<T> {
  const isObject = (value: unknown): value is object =>
    value !== null && typeof value === "object" && !Array.isArray(value);

  const camelCase = (str: string): string =>
    str.replace(/[-_](\w)/g, (_, char) => char.toUpperCase());

  return Object.keys(obj).reduce((acc, key) => {
    const camelKey = camelCase(key) as keyof CamelCased<T>;
    const value = obj[key as keyof T];

    acc[camelKey] = (
      Array.isArray(value)
        ? value.map((item) => (isObject(item) ? camelCaseKeys(item) : item))
        : isObject(value)
          ? camelCaseKeys(value)
          : value
    ) as CamelCased<T>[keyof CamelCased<T>];

    return acc;
  }, {} as CamelCased<T>);
}

export function formatDate(date: string): string {
  if (!date) {
    return "";
  }

  const parsedDate = new Date(date);

  if (isNaN(parsedDate.getTime())) {
    return "";
  }

  return parsedDate.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}
